"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useModule } from "@/hooks/use-module";
import { useTimezone } from "@/hooks/use-timezone";
import { clinicClockFor, dayMarker } from "@/lib/clinic-clock";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { UserType } from "@/types/enums";
import WaitlistMatchDialog, { getMatchingWaitlistEntries } from "./waitlist-match-dialog";
import { followUpSidebarDefinition } from "@/features/sidebar/scheduling-sidebar";
import { useWaitlistForProvidersDate } from "@/query/waitlist-query";
import { useFollowUpSidebarHostStore } from "@/stores/follow-up-sidebar-host-store";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
import { useWaitlistCancellationStore } from "@/stores/waitlist-cancellation-store";
function openFollowUp(requestedDay: Date) {
  useRightSidebarStore.getState().open(followUpSidebarDefinition, {
    requestedDay
  });
}

// Mount outside PatientSidebarPanel so cancellation/calendar refetches cannot
// remove the query observer or the follow-up dialog.
export default function CancellationWaitlistDialog() {
  const $ = _c(57);
  const {
    tenant
  } = useTenant();
  const {
    currentUser
  } = useUser();
  const viewerTimezone = useTimezone();
  const hasWaitlistModule = useModule("WAITLIST");
  let t0;
  if ($[0] !== currentUser || $[1] !== hasWaitlistModule) {
    t0 = hasWaitlistModule && hasPermission(currentUser, PERMISSIONS.SCHEDULING_WAITLIST_MANAGE);
    $[0] = currentUser;
    $[1] = hasWaitlistModule;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const canManageWaitlist = t0;
  const {
    event,
    tenantSubDomain,
    cancelled,
    dismiss
  } = useWaitlistCancellationStore();
  const activeEvent = tenant?.subDomain === tenantSubDomain ? event : null;
  let t1;
  if ($[3] !== activeEvent?.attendees) {
    t1 = activeEvent?.attendees.filter(_temp).map(_temp2) ?? [];
    $[3] = activeEvent?.attendees;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const providerIds = t1;
  const date = activeEvent ? new Date(activeEvent.start) : new Date();
  const timezone = activeEvent?.location && "timezone" in activeEvent.location ? activeEvent.location.timezone ?? viewerTimezone : viewerTimezone;
  const clock = clinicClockFor(timezone);
  const cancelledDay = clock.dayOf(date);
  let t2;
  if ($[5] !== activeEvent) {
    t2 = activeEvent?.location?.id ? [activeEvent.location.id] : undefined;
    $[5] = activeEvent;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    data,
    isLoading,
    isError
  } = useWaitlistForProvidersDate(providerIds, clock.dayOf(date), !!activeEvent && canManageWaitlist, t2);
  const unavailable = isError || data === null;
  const matches = getMatchingWaitlistEntries(data, date, undefined, activeEvent?.location?.id, undefined, clock);
  let t3;
  if ($[7] !== dismiss || $[8] !== event || $[9] !== tenant?.subDomain || $[10] !== tenantSubDomain) {
    t3 = () => {
      if (event && tenant?.subDomain !== tenantSubDomain) {
        dismiss();
      }
    };
    $[7] = dismiss;
    $[8] = event;
    $[9] = tenant?.subDomain;
    $[10] = tenantSubDomain;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const t4 = tenant?.subDomain;
  let t5;
  if ($[12] !== dismiss || $[13] !== event || $[14] !== t4 || $[15] !== tenantSubDomain) {
    t5 = [event, t4, tenantSubDomain, dismiss];
    $[12] = dismiss;
    $[13] = event;
    $[14] = t4;
    $[15] = tenantSubDomain;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  useEffect(t3, t5);
  const followUpOnScreen = useFollowUpSidebarHostStore(_temp3);
  let t6;
  if ($[17] !== activeEvent || $[18] !== cancelled || $[19] !== cancelledDay || $[20] !== dismiss || $[21] !== followUpOnScreen || $[22] !== isLoading || $[23] !== matches?.length || $[24] !== unavailable) {
    t6 = () => {
      if (!cancelled || !activeEvent || !followUpOnScreen) {
        return;
      }
      if (!isLoading && (unavailable || matches?.length)) {
        openFollowUp(dayMarker(cancelledDay));
      }
      if (!isLoading) {
        dismiss();
      }
    };
    $[17] = activeEvent;
    $[18] = cancelled;
    $[19] = cancelledDay;
    $[20] = dismiss;
    $[21] = followUpOnScreen;
    $[22] = isLoading;
    $[23] = matches?.length;
    $[24] = unavailable;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const t7 = matches?.length;
  let t8;
  if ($[26] !== activeEvent || $[27] !== cancelled || $[28] !== cancelledDay || $[29] !== dismiss || $[30] !== followUpOnScreen || $[31] !== isLoading || $[32] !== t7 || $[33] !== unavailable) {
    t8 = [cancelled, activeEvent, followUpOnScreen, isLoading, unavailable, t7, cancelledDay, openFollowUp, dismiss];
    $[26] = activeEvent;
    $[27] = cancelled;
    $[28] = cancelledDay;
    $[29] = dismiss;
    $[30] = followUpOnScreen;
    $[31] = isLoading;
    $[32] = t7;
    $[33] = unavailable;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  useEffect(t6, t8);
  let t9;
  if ($[35] !== activeEvent || $[36] !== cancelled || $[37] !== dismiss || $[38] !== isLoading || $[39] !== matches?.length || $[40] !== unavailable) {
    t9 = () => {
      if (cancelled && activeEvent && !isLoading && !unavailable && !matches?.length) {
        dismiss();
      }
    };
    $[35] = activeEvent;
    $[36] = cancelled;
    $[37] = dismiss;
    $[38] = isLoading;
    $[39] = matches?.length;
    $[40] = unavailable;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  const t10 = matches?.length;
  let t11;
  if ($[42] !== activeEvent || $[43] !== cancelled || $[44] !== dismiss || $[45] !== isLoading || $[46] !== t10 || $[47] !== unavailable) {
    t11 = [cancelled, activeEvent, isLoading, unavailable, t10, dismiss];
    $[42] = activeEvent;
    $[43] = cancelled;
    $[44] = dismiss;
    $[45] = isLoading;
    $[46] = t10;
    $[47] = unavailable;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  useEffect(t9, t11);
  if (!canManageWaitlist || !activeEvent || !cancelled || providerIds.length === 0 || followUpOnScreen) {
    return null;
  }
  if (!isLoading && !unavailable && !matches?.length) {
    return null;
  }
  let t12;
  if ($[49] !== dismiss) {
    t12 = open => {
      if (!open) {
        dismiss();
      }
    };
    $[49] = dismiss;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  const t13 = activeEvent.location?.id;
  let t14;
  if ($[51] !== date || $[52] !== providerIds || $[53] !== t12 || $[54] !== t13 || $[55] !== timezone) {
    t14 = <WaitlistMatchDialog open={true} onOpenChange={t12} providerIds={providerIds} date={date} locationId={t13} timezone={timezone} cancelledAppointment={true} />;
    $[51] = date;
    $[52] = providerIds;
    $[53] = t12;
    $[54] = t13;
    $[55] = timezone;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  return t14;
}
function _temp3(s) {
  return s.hostMounted;
}
function _temp2(attendee_0) {
  return attendee_0.user.id;
}
function _temp(attendee) {
  return attendee.user.type === UserType.PROVIDER;
}